import { type FormEvent, useEffect, useState } from "react";
import { Link } from "react-router";

import {
  RUN_STATES,
  type RunState,
  type RunSummary,
  listRuns,
  message,
  runPath,
} from "./runsClient";

const FINISHED = new Set(["completed", "completed_with_exceptions", "skipped"]);

/** Corpus Runs (DESIGN-SPEC 9.1, 15): every run with its state and stage progress; each run
 * opens its dashboard. */
export function CorpusRunsPage() {
  const [filter, setFilter] = useState<{ engagement_id?: string; state?: RunState }>({});
  const [found, setFound] = useState<RunSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    listRuns(filter)
      .then((runs) => {
        if (live) {
          setFound(runs);
          setError(null);
        }
      })
      .catch((caught: unknown) => {
        if (live) setError(message(caught));
      });
    return () => {
      live = false;
    };
  }, [filter]);

  function onFilter(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const engagement = String(form.get("engagement") ?? "").trim();
    const state = String(form.get("state") ?? "");
    setFilter({
      engagement_id: engagement || undefined,
      state: (state || undefined) as RunState | undefined,
    });
  }

  return (
    <section aria-labelledby="runs-title">
      <h1 id="runs-title">Corpus Runs</h1>
      <form onSubmit={onFilter} aria-label="Run filters">
        <label>
          Engagement id <input name="engagement" defaultValue={filter.engagement_id ?? ""} />
        </label>
        <label>
          State{" "}
          <select name="state" defaultValue={filter.state ?? ""}>
            <option value="">Any</option>
            {RUN_STATES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </label>
        <button type="submit">Show runs</button>
      </form>
      {error && <p role="alert">Could not load runs: {error}</p>}
      {found === null && !error && <p>Loading…</p>}
      {found && found.length === 0 && <p>No Corpus Runs match.</p>}
      {found && found.length > 0 && (
        <table className="grid" aria-label="Corpus Runs">
          <thead>
            <tr>
              <th>Run</th>
              <th>State</th>
              <th>Stages finished</th>
              <th>Created</th>
              <th>Started</th>
              <th>Ended</th>
            </tr>
          </thead>
          <tbody>
            {found.map((run) => (
              <tr key={run.id}>
                <td>
                  <Link to={runPath(run.id)}>{run.run_key}</Link>
                  {run.description ? ` · ${run.description}` : ""}
                </td>
                <td className={`status-${run.state}`}>
                  {run.state}
                  {run.state_reason ? ` (${run.state_reason})` : ""}
                </td>
                <td>
                  {run.stages.filter((s) => FINISHED.has(s.state)).length} of {run.stages.length}
                </td>
                <td>{run.created_at}</td>
                <td>{run.started_at ?? ""}</td>
                <td>{run.ended_at ?? ""}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
